import { useState, useEffect } from 'react';

const CURRENT_USER_KEY = 'current_user';

interface StoredUser {
  id?: number;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  role?: 'user' | 'admin' | 'superadmin';
  allowedTenants?: { id: number; name: string }[];
  [key: string]: unknown;
}

function getDisplayName(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CURRENT_USER_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw) as StoredUser;
    const parts = [user?.firstName, user?.lastName].filter(
      (p): p is string => typeof p === 'string' && p.trim().length > 0,
    );
    if (parts.length > 0) return parts.join(' ');
    if (user?.email && typeof user.email === 'string' && user.email.trim()) {
      return user.email.trim();
    }
    return null;
  } catch {
    return null;
  }
}

function getStoredUser(): StoredUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CURRENT_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

export function useCurrentUser() {
  const [id, setId] = useState<number | null>(() => getStoredUser()?.id ?? null);
  const [displayName, setDisplayName] = useState<string | null>(() => getDisplayName());
  const [email, setEmail] = useState<string | null>(() => getStoredUser()?.email ?? null);
  const [role, setRole] = useState<'user' | 'admin' | 'superadmin'>(() => {
    const r = getStoredUser()?.role;
    if (r === 'admin' || r === 'superadmin' || r === 'user') return r;
    return 'user';
  });
  const [allowedTenants, setAllowedTenants] = useState<{ id: number; name: string }[]>(
    () => getStoredUser()?.allowedTenants ?? [],
  );

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === CURRENT_USER_KEY) {
        setDisplayName(getDisplayName());
        const user = getStoredUser();
        setId(user?.id ?? null);
        setEmail(user?.email ?? null);
        {
          const r = user?.role;
          setRole(
            r === 'admin' || r === 'superadmin' || r === 'user' ? r : 'user',
          );
        }
        setAllowedTenants(user?.allowedTenants ?? []);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return { id, displayName, email, role, allowedTenants };
}
